import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useState } from 'react';

type HistoryEntry = {
  action: string; // 'created' | 'changed'
  field_label: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by: string;
  changed_at: number;
};

/** Relativní čas v češtině (best-effort). */
function relTime(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'právě teď';
  if (min < 60) return `před ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `před ${h} h`;
  const d = Math.floor(h / 24);
  return `před ${d} ${d === 1 ? 'dnem' : 'dny'}`;
}

/**
 * Přehled změn (náš audit log) jednoho záznamu — časová osa: založení a
 * jednotlivé změny polí (kdo, kdy, staré → nové).
 */
export function HistoryModal({
  table,
  recordId,
  onClose,
}: {
  table: string;
  recordId: string;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ history: HistoryEntry[] }>(`/api/vyroba/tabidoo/${table}/${recordId}/history`)
      .then((d) => {
        if (!cancelled) setHistory(d.history);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : 'Chyba načítání historie');
      });
    return () => {
      cancelled = true;
    };
  }, [table, recordId]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-lg my-10 bg-surface rounded-2xl shadow-modal reveal flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-bold text-text">Přehled změn</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-text text-xl leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {err && <p className="text-sm text-danger">{err}</p>}
          {history == null && !err && <p className="text-sm text-text-muted">Načítám…</p>}
          {history?.length === 0 && (
            <p className="text-sm text-text-muted">
              Zatím žádné změny evidované v naší aplikaci. (Historie se vede od zavedení této funkce
              — starší úpravy z Tabidoo tu nejsou.)
            </p>
          )}
          <ol className="flex flex-col">
            {(history ?? []).map((h, i) => {
              const isCreated = h.action === 'created';
              return (
                <li key={`${h.changed_at}-${i}`} className="flex gap-3">
                  {/* Osa s ikonou */}
                  <div className="flex flex-col items-center">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                        isCreated
                          ? 'border-success/40 text-success bg-success-light'
                          : 'border-primary/40 text-primary bg-primary-50'
                      }`}
                    >
                      {isCreated ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8" />
                          <path d="M12 7v5l3 3" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
                        </svg>
                      )}
                    </span>
                    {i < (history?.length ?? 0) - 1 && (
                      <span className="w-px flex-1 bg-border-light my-1" />
                    )}
                  </div>
                  {/* Obsah */}
                  <div className="pb-6 min-w-0 flex-1">
                    <div className="text-xs text-text-muted mb-1">{relTime(h.changed_at)}</div>
                    {isCreated ? (
                      <div className="text-sm font-semibold text-text">
                        Byl vytvořen nový záznam.
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm font-semibold text-text">
                          {h.field_label ?? 'Změna'}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="rounded bg-bg border border-border px-2 py-0.5 text-xs text-text-secondary">
                            {h.old_value || '—'}
                          </span>
                          <span className="text-text-muted">→</span>
                          <span className="rounded bg-primary-50 border border-primary/30 px-2 py-0.5 text-xs font-semibold text-text">
                            {h.new_value || '—'}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="text-xs text-text-muted mt-1">{h.changed_by}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="flex justify-end px-6 py-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="bg-primary hover:bg-primary-dark text-white text-sm font-semibold rounded-[5px] px-5 py-2 cursor-pointer transition-colors"
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
